import { z } from 'zod';

import { jobProfileComponentSchema } from '~/shared/data-contract/job-profile';

export const translationTextSchema = z.object({
    locale: z.string(),
    text: z.string(),
});

export type TranslationText = z.infer<typeof translationTextSchema>;

export const skillLimitsSchema = z.object({
    expectedNumberOfSkills: z.number().min(5).max(50),
    maximumNumberOfSkills: z.number().min(5).max(70),
});

export type SkillLimits = z.infer<typeof skillLimitsSchema>;

export const jobProfileStructureTypeSchema = z.enum(['BULLET_POINTS', 'SENTENCES']);

export type JobProfileStructureType = z.infer<typeof jobProfileStructureTypeSchema>;

export const jobProfileTemplateTypeSchema = z.enum(['COBRAINER', 'CUSTOM']);

export type JobProfileTemplateType = z.infer<typeof jobProfileTemplateTypeSchema>;

// --- Section structure ---

export const customJobProfileTemplateStructureSchema = z.object({
    name: translationTextSchema.array(),
    guidance: z.string(),
    type: jobProfileStructureTypeSchema,
    minNumberOfItems: z.number().min(0),
    maxNumberOfItems: z.number().min(0),
});

export type CustomJobProfileTemplateStructure = z.infer<typeof customJobProfileTemplateStructureSchema>;

// --- Section request/response ---

export const customJobProfileTemplateSectionRequestSchema = z.object({
    id: z.string().uuid(),
    structure: customJobProfileTemplateStructureSchema,
    type: jobProfileTemplateTypeSchema,
});

export type CustomJobProfileTemplateSectionRequest = z.infer<typeof customJobProfileTemplateSectionRequestSchema>;

export const customJobProfileTemplateSectionResponseSchema = z.object({
    id: z.string().uuid(),
    structure: customJobProfileTemplateStructureSchema,
    type: jobProfileTemplateTypeSchema,
});

export type CustomJobProfileTemplateSectionResponse = z.infer<typeof customJobProfileTemplateSectionResponseSchema>;

export const customJobProfileTemplateRequestSchema = z.object({
    name: z.string().max(512),
    description: z.string().max(5000),
    jobRoleSections: customJobProfileTemplateSectionRequestSchema.array().min(1),
    jobLevelSections: customJobProfileTemplateSectionRequestSchema.array().min(1),
    roleSkillLimits: skillLimitsSchema,
    levelSkillLimits: skillLimitsSchema,
});

export type CustomJobProfileTemplateRequest = z.infer<typeof customJobProfileTemplateRequestSchema>;

const userShortSchema = z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    emailAddress: z.string().optional(),
});

export const customJobProfileTemplateResponseSchema = z.object({
    id: z.string().uuid(),
    cobrainerJobTemplateId: z.string().uuid().nullable().optional(),
    name: z.string(),
    description: z.string(),
    jobArchitectureId: z.string().uuid(),
    createdBy: userShortSchema.nullable().optional(),
    createdAt: z.string(),
    jobRoleSections: customJobProfileTemplateSectionResponseSchema.array(),
    jobLevelSections: customJobProfileTemplateSectionResponseSchema.array(),
    roleSkillLimits: skillLimitsSchema,
    levelSkillLimits: skillLimitsSchema,
});

export type CustomJobProfileTemplateResponse = z.infer<typeof customJobProfileTemplateResponseSchema>;

export const customJobProfileTemplateGetResponseSchema = z
    .union([
        customJobProfileTemplateResponseSchema,
        z.object({ template: customJobProfileTemplateResponseSchema.nullable() }),
    ])
    .transform((value) => ('template' in value ? value.template : value));

export const managedJobProfileTemplateResponseSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string(),
    activatedAt: z.string(),
    jobRoleSections: customJobProfileTemplateSectionResponseSchema.array(),
    jobLevelSections: customJobProfileTemplateSectionResponseSchema.array(),
    roleSkillLimits: skillLimitsSchema,
    levelSkillLimits: skillLimitsSchema,
});

export type ManagedJobProfileTemplateResponse = z.infer<typeof managedJobProfileTemplateResponseSchema>;

export const managedJobProfileTemplateResponseArraySchema = managedJobProfileTemplateResponseSchema.array();

export const jobProfileTemplatePreviewRequestSchema = z.object({
    jobRoleSections: customJobProfileTemplateSectionRequestSchema.array(),
    jobLevelSections: customJobProfileTemplateSectionRequestSchema.array(),
    roleSkillLimits: skillLimitsSchema,
    levelSkillLimits: skillLimitsSchema,
});

export type JobProfileTemplatePreviewRequest = z.infer<typeof jobProfileTemplatePreviewRequestSchema>;

export const previewStructuredSectionSchema = z.object({
    tag: z.string(),
    value: z.string(),
});

export const previewSkillSchema = z.object({
    id: z.string(),
    title: z.string(),
    skillType: z.enum(['hard', 'soft', 'language']),
    level: z.number().optional(),
    competencyId: z.string().optional(),
});

export const previewCompetencySchema = z.object({
    id: z.string(),
    title: z.string(),
});

export const previewJobItemSchema = jobProfileComponentSchema;

export const jobProfileTemplatePreviewResponseSchema = z.object({
    jobRole: previewJobItemSchema.optional(),
    jobLevel: previewJobItemSchema.optional(),
});

export type PreviewJobItem = z.infer<typeof previewJobItemSchema>;
export type PreviewSkill = z.infer<typeof previewSkillSchema>;
export type PreviewCompetency = z.infer<typeof previewCompetencySchema>;
export type JobProfileTemplatePreviewResponse = z.infer<typeof jobProfileTemplatePreviewResponseSchema>;
